// src/pages/admin/user/UserDetail.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";

export default function UserDetail() {
  const { id } = useParams(); // userId
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const fmtDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
  };

  const toUserTypeLabel = (type) => {
    if (type === "ADMIN") return "관리자";
    if (type === "USER") return "일반";
    return type || "-";
  };

  const loadUser = async () => {
    setLoading(true);
    setError("");
    try {
      // ✅ /api/user/detail/{id} 기준
      const res = await api.get(`/api/user/detail/${id}`);
      const resultCode = res.data?.resultCode;

      if (resultCode !== "200" && resultCode !== 200) {
        setError(res.data?.resultMessage || "계정 정보를 불러오지 못했습니다.");
        return;
      }

      setUser(res.data?.data || null);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        setError("로그인이 필요합니다.");
        navigate("/login");
      } else {
        setError(
          err.response?.data?.resultMessage ||
            "계정 정보를 불러오는 중 오류가 발생했습니다."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ✅ 삭제 처리
  const handleDelete = async () => {
    if (!user) return;

    const ok = window.confirm(
      `"${user.name}" 계정을 정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      // ⚠️ 백엔드 삭제 엔드포인트에 맞춰서 사용
      // 예시: @DeleteMapping("/api/user/{userId}")
      const res = await api.delete(`/api/user/delete/${user.userId}`);
      const resultCode = res.data?.resultCode;

      if (resultCode !== "200" && resultCode !== 200) {
        alert(res.data?.resultMessage || "계정 삭제에 실패했습니다.");
        return;
      }

      alert("계정이 삭제되었습니다.");
      navigate("/admin/user/list");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        alert("로그인이 만료되었습니다. 다시 로그인해 주세요.");
        navigate("/login");
      } else {
        alert(
          err.response?.data?.resultMessage ||
            "계정 삭제 중 오류가 발생했습니다."
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-12 gap-3 border-t px-4 py-3 text-sm">
      <div className="col-span-12 font-semibold text-slate-700 md:col-span-3">
        {label}
      </div>
      <div className="col-span-12 text-slate-800 md:col-span-9">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
          계정 정보를 불러오는 중입니다...
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border bg-white p-8 text-center text-red-600">
          {error}
          <div className="mt-4">
            <button
              onClick={() => navigate("/admin/user/list")}
              className="rounded-lg border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              목록으로
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
          계정 정보를 찾을 수 없습니다.
          <div className="mt-4">
            <button
              onClick={() => navigate("/admin/user/list")}
              className="rounded-lg border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              목록으로
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* 헤더 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
            계정 상세
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            선택한 계정의 상세 정보를 확인합니다.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/user/list"
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            목록으로
          </Link>
          {/* ✅ 삭제 버튼 */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {deleting ? "삭제 중..." : "삭제"}
          </button>
        </div>
      </div>

      {/* 상세 카드 */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
          기본 정보
        </div>
        <Row label="No">{user.userId}</Row>
        <Row label="이메일">{user.email}</Row>
        <Row label="이름">{user.name}</Row>
        <Row label="계정 유형">{toUserTypeLabel(user.userType)}</Row>
        <Row label="생년월일">{fmtDate(user.birthDate)}</Row>
        <Row label="전화번호">{user.phone || "-"}</Row>
        <Row label="주소">{user.address || "-"}</Row>
        <Row label="생성일자">{fmtDateTime(user.createdAt)}</Row>
      </div>
    </AdminLayout>
  );
}
